import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Button from 'react-bootstrap/Button';
import StorageAddForm from './StorageAddForm';
import StorageMapNode from './StorageMapNode';
import {
  buildStorageFlowLayout,
  countChildren,
  countLotsInside,
  isSlottedBox,
  storageNodeShiftY,
} from '../../utils/storageTree';
import './StorageExplorerStyle.css';

/** Registered once: re-creating this object each render remounts every node. */
const NODE_TYPES = { storageNode: StorageMapNode };

/**
 * The whole storage tree as a draggable flowchart.
 *
 * Layout comes from `buildStorageFlowLayout` (a row per depth, rooms on top). After that
 * React Flow owns node positions so the user can drag freely; positions already
 * moved by hand are preserved when the tree changes, so adding a box does not
 * throw away the arrangement.
 *
 * @param {Object} props
 * @param {import('../../utils/storageTree').StorageNode[]} props.nodes
 * @param {string|null} props.selectedId
 * @param {Function} props.onSelect - (nodeId|null) => void
 * @param {Function} props.onAdd - (parent|null, { name, subtype }) => void
 * @param {Function} props.onRemove - (token, nodeId) => Promise<{ success, toast }|string[]>
 * @param {Function} [props.onDeleted] - (node) => void. Drops the place after a confirmed delete
 * @param {Function} [props.onOpenNode] - (node) => void. Any place opens what's inside
 * @param {Function} [props.onOpenBox] - (node) => void. Slotted boxes open their chart
 * @param {Function} [props.onEditBox] - (node, { name, slotted, slotCount }) => boolean|Promise
 * @param {Function} [props.onMove] - (node, parentId) => boolean|Promise. Reparent a place
 * @param {Function} [props.onPlaceItem] - (node) => void. A shelf, drawer, rack, or box opens the inventory search
 * @param {Object<string, number>|null} [props.lotCounts] - Direct lots on each shelf or box. Null when the tree has no `lot_count`
 * @param {Function} [props.onPreviewPlace] - (node) => void. Hover starts loading that place's lots
 * @param {Function} [props.onCancelPreview] - (node) => void. Leaving the card cancels a hover that has not finished
 * @param {number} [props.fitSignal] - Bump to re-fit the view (breadcrumb jumps)
 * @param {{ current: Map }} [props.dragPositionsRef] - Owned by the page so dragged
 *   positions survive a switch to the list view and back
 */
const StorageMapCanvas = ({
  nodes,
  selectedId,
  onSelect,
  onAdd,
  onRemove,
  onDeleted,
  onOpenNode,
  onOpenBox,
  onEditBox,
  onMove,
  onRename,
  onPlaceItem,
  lotCounts = null,
  fitSignal = 0,
  onPreviewPlace,
  onCancelPreview,
  dragPositionsRef = null,
}) => {
  const layout = useMemo(() => buildStorageFlowLayout(nodes), [nodes]);
  const { fitView } = useReactFlow();
  const [addingRoom, setAddingRoom] = useState(false);
  // Positions the user dragged, so a tree change does not reset the map.
  const ownDraggedRef = useRef(new Map());
  const draggedRef = dragPositionsRef || ownDraggedRef;
  // Height of each card while its form is closed, so the open form's extra
  // pixels can push the cards underneath it.
  const collapsedHeightRef = useRef(new Map());
  const [openExtras, setOpenExtras] = useState({});

  const handleSizeChange = useCallback((id, height, formOpen) => {
    if (!formOpen) {
      collapsedHeightRef.current.set(id, height);
      setOpenExtras(prev => {
        if (prev[id] == null) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    const collapsed = collapsedHeightRef.current.get(id);
    const extra = collapsed == null ? 0 : Math.max(0, height - collapsed);
    setOpenExtras(prev => (prev[id] === extra ? prev : { ...prev, [id]: extra }));
  }, []);

  const flowNodes = useMemo(() => {
    const basePosition = item => draggedRef.current.get(item.id) || item.position;
    const openings = Object.entries(openExtras)
      .map(([id, extra]) => {
        const item = layout.nodes.find(entry => entry.id === id);
        if (!item || !extra) return null;
        const position = basePosition(item);
        return { id, x: position.x, y: position.y, extra };
      })
      .filter(Boolean);

    return layout.nodes.map(item => {
      const position = basePosition(item);
      const shift = storageNodeShiftY(position, item.id, openings);
      return {
        id: item.id,
        type: 'storageNode',
        position: shift ? { x: position.x, y: position.y + shift } : position,
        zIndex: openExtras[item.id] ? 10 : 0,
        selected: selectedId != null && String(selectedId) === item.id,
        data: {
          node: item.node,
          childCount: countChildren(nodes, item.node.id),
          lotCount: lotCounts
            ? countLotsInside(nodes, item.node.id, lotCounts)
            : null,
          onAdd,
          onRemove,
          onDeleted,
          onOpenBox,
          nodes,
          onEditBox,
          onMove,
          onRename,
          onPlaceItem,
          onSizeChange: handleSizeChange,
        },
      };
    });
  }, [layout, nodes, lotCounts, selectedId, onAdd, onRemove, onDeleted, onOpenBox, onEditBox, onMove, onRename, onPlaceItem, openExtras, handleSizeChange, draggedRef]);

  const flowEdges = useMemo(
    () =>
      layout.edges.map(edge => ({
        ...edge,
        type: 'smoothstep',
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
        animated: false,
      })),
    [layout]
  );

  const [renderNodes, setRenderNodes, onNodesChange] = useNodesState(flowNodes);
  const [renderEdges, setRenderEdges, onEdgesChange] = useEdgesState(flowEdges);

  useEffect(() => {
    setRenderNodes(flowNodes);
  }, [flowNodes, setRenderNodes]);

  useEffect(() => {
    setRenderEdges(flowEdges);
  }, [flowEdges, setRenderEdges]);

  // Fit when the breadcrumb jumps somewhere, not on every drag.
  useEffect(() => {
    if (!fitSignal) return;
    fitView({ duration: 300, padding: 0.2 });
  }, [fitSignal, fitView]);

  const handleNodeDragStop = useCallback(
    (_event, node) => {
      draggedRef.current.set(node.id, node.position);
    },
    [draggedRef]
  );

  return (
    <ReactFlow
      nodes={renderNodes}
      edges={renderEdges}
      nodeTypes={NODE_TYPES}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeDragStop={handleNodeDragStop}
      onNodeClick={(_event, node) => {
        onSelect?.(node.id);
        const storageNode = node.data?.node;
        if (!storageNode) return;
        if (onOpenNode) onOpenNode(storageNode);
        else if (isSlottedBox(storageNode)) onOpenBox?.(storageNode);
      }}
      onNodeMouseEnter={(_event, node) => onPreviewPlace?.(node.data?.node)}
      onNodeMouseLeave={(_event, node) => onCancelPreview?.(node.data?.node)}
      onPaneClick={() => onSelect?.(null)}
      /* The tree is built through the add form, not by dragging connections. */
      nodesConnectable={false}
      edgesFocusable={false}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      minZoom={0.2}
      maxZoom={1.75}
      proOptions={{ hideAttribution: false }}
    >
      <Background variant={BackgroundVariant.Dots} gap={22} size={1} />
      {typeof onAdd === 'function' ? (
      <Panel position="top-left" className="storage-map-add nodrag nopan">
        {addingRoom ? (
          <>
            <StorageAddForm
              parent={null}
              compact
              idPrefix="map-add-room"
              onAdd={async payload => {
                const ok = await onAdd?.(null, payload);
                if (ok !== false) setAddingRoom(false);
              }}
            />
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => setAddingRoom(false)}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            className="glass-primary-btn"
            onClick={() => setAddingRoom(true)}
          >
            + Add room
          </Button>
        )}
      </Panel>
      ) : null}
      <Controls showInteractive={false} />
      {renderNodes.length > 6 ? <MiniMap pannable zoomable /> : null}
    </ReactFlow>
  );
};

/**
 * Storage map with its own React Flow provider, so `useReactFlow` (fit view)
 * works and the page does not need to know about the provider.
 *
 * @param {Object} props - See `StorageMapCanvas`
 */
const StorageMap = props => (
  <div className="storage-map">
    <ReactFlowProvider>
      <StorageMapCanvas {...props} />
    </ReactFlowProvider>
  </div>
);

export default StorageMap;
